import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Nullable } from "../../api/types";
import { twMerge } from "tailwind-merge";

export type OptionGroup = {
    title: string;
    options: string[];
};

export type DropdownOptions = string[] | OptionGroup[];

export default function InterpretationDropdownInput({
    value,
    previuosValue,
    options,
    size = "md",
    label,
    isDisabled,
    onSelect,
    getIsOptionDisabled,
}: {
    value: Nullable<string>;
    previuosValue: Nullable<string>;
    options: DropdownOptions;
    size?: "lg" | "md";
    label: string;
    isDisabled?: boolean;
    onSelect: (value: Nullable<string>) => void;
    getIsOptionDisabled?: (option: string) => boolean;
}) {
    const listboxButtonRef = useRef<HTMLButtonElement>(null);
    const [width, setWidth] = useState("auto");

    useEffect(() => {
        function setListboxWidth() {
            if (listboxButtonRef.current) {
                setWidth(listboxButtonRef.current.offsetWidth + "px");
            }
        }

        setListboxWidth();
        window.addEventListener("resize", setListboxWidth);

        return () => {
            window.removeEventListener("resize", setListboxWidth);
        };
    }, []);

    const handleChange = (selectedOption: string | null) => {
        try {
            onSelect(selectedOption || null);
        } catch (e) {
            console.error(e);
            alert("Error selecting option");
        }
    };

    // Helper function to check if options are grouped
    const isGroupedOptions = (opts: DropdownOptions): opts is OptionGroup[] => {
        return (
            Array.isArray(opts) &&
            opts.length > 0 &&
            typeof opts[0] === "object" &&
            "title" in opts[0]
        );
    };

    // Find the selected option object for Listbox
    const selectedOption = value || null;

    return (
        <div
            className={`${
                size === "lg"
                    ? "font-bold text-xl xl:text-2xl"
                    : "font-semibold text-base xl:text-xl"
            }  text-white w-full h-9 xl:h-10 rounded-md flex items-center gap-3`}
        >
            <label className="shrink-0 w-[295px] xl:w-[353px] truncate">
                {label}
            </label>
            <Listbox
                value={selectedOption}
                onChange={handleChange}
                disabled={isDisabled}
            >
                <ListboxButton
                    className={`bg-white/20 w-full h-full flex items-center justify-between rounded-sm pt-[5px] pb-1 px-2.5`}
                    ref={listboxButtonRef}
                >
                    <span className="w-full text-start line-clamp-1">
                        {(value as ReactNode) || "Select"}
                    </span>
                    <ChevronDownIcon className="w-4 h-4" />
                </ListboxButton>
                <ListboxOptions
                    className={`flex flex-col shadow-[0px_4px_4px_0px_#00000025] bg-white rounded-b-md`}
                    anchor="bottom start"
                    style={{ width: width, minWidth: "500px" }}
                >
                    {isGroupedOptions(options)
                        ? // Render grouped options
                          options.map((group) => (
                              <div key={group.title}>
                                  <div className="px-2.5 py-1.5 pb-0 text-lg font-bold">
                                      {group.title}
                                  </div>
                                  {group.options.map((option) => {
                                      const isOptionDisabled =
                                          getIsOptionDisabled?.(option);
                                      const isSelected = option === value;
                                      return (
                                          <ListboxOption
                                              key={option}
                                              value={option}
                                              disabled={isOptionDisabled}
                                              className={twMerge(
                                                  "shrink-0 py-1.5 leading-tight flex items-center px-2.5 text-blue-dark font-semibold bg-white w-full text-left cursor-pointer data-[focus]:bg-blue-option-select",
                                                  isOptionDisabled &&
                                                      "text-gray-400 cursor-not-allowed",
                                                  isSelected &&
                                                      "bg-blue-option-select"
                                              )}
                                          >
                                              {option}
                                          </ListboxOption>
                                      );
                                  })}
                              </div>
                          ))
                        : // Render simple options
                          (options as string[]).map((option) => {
                              const isOptionDisabled =
                                  getIsOptionDisabled?.(option);
                              const isSelected = option === value;
                              return (
                                  <ListboxOption
                                      key={option}
                                      value={option}
                                      disabled={isOptionDisabled}
                                      className={twMerge(
                                          "shrink-0 py-1.5 leading-tight flex items-center px-2.5 text-blue-dark font-semibold bg-white w-full text-left cursor-pointer data-[focus]:bg-blue-option-select",
                                          isOptionDisabled &&
                                              "text-gray-400 cursor-not-allowed",
                                          isSelected && "bg-blue-option-select"
                                      )}
                                  >
                                      {option}
                                  </ListboxOption>
                              );
                          })}
                    {previuosValue ? (
                        <div className="border-t border-[#999999] py-1.5 leading-tight flex items-center px-2.5 text-blue-dark font-semibold bg-white w-full text-left">
                            Automatic interpretation:{" "}
                            {previuosValue as ReactNode}
                        </div>
                    ) : null}
                </ListboxOptions>
            </Listbox>
        </div>
    );
}
